import React, { memo, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button, Col, Layout, Row, Tooltip } from 'antd';
import { ReactComponent as ArrowLeftSvg } from '@assets/icons/arrow-left-without-line.svg';
import { ReactComponent as LogoSvg } from '@assets/icons/logo-white.svg';
import { ReactComponent as DownloadSvg } from '@assets/icons/bulkActions/download.svg';
import { ReactComponent as LinkSvg } from '@assets/icons/dropdown/link.svg';
import { ReactComponent as ArrowDownSvg } from '@assets/icons/arrow-down.svg';
import { ReactComponent as FolderSvg } from '@assets/icons/folder.svg';
import { ReactComponent as PrivateFolderSvg } from '@assets/icons/folder-private.svg';
import { ReactComponent as DownloadInvoiceSvg } from '@assets/icons/download-invoice.svg';
import TilesView from './components/TilesView/TilesView';
import ListView from './components/ListView/ListView';
import { downloadAll } from '@helpers/download';
import {
  filterAssetsBySearchText,
  groupByFolder,
  getItemsInFolder
} from '@pages/Campaigns/CampaignReview/utils';
import ControlPanel from '@pages/ExternalReviewer/ControlPanel';
import LottieComponent from '@components/Lottie';
import { useDebounced } from '@helpers/useDebounced';
import './AssetsList.less';
import { useWebSocketDebouncedSubscription } from '@hooks/useWebSocketClient';
import { useShareReview } from '@components/ShareReviewProvider';

const { Header, Content } = Layout;

const SEARCH_PARAM = 'search';
const VIEW_PARAM = 'view';
const FOLDER_PARAM = 'folder';

export default memo(function AssetsList() {
  const { reviewId, content, refetch } = useShareReview();
  const [searchParams, setSearchParams] = useSearchParams();
  const searchQuery = (searchParams.get(SEARCH_PARAM) ?? '')
    .trim()
    .toLowerCase();
  const viewValue = searchParams.get(VIEW_PARAM) ?? 'tiles';
  const searchText = useDebounced(searchQuery, 250);
  const folderId = searchParams.get(FOLDER_PARAM) ?? '';

  const assetsGroupedByFolder = useMemo(() => {
    const assets = content?.assets || [];
    if (!assets.length) return { folders: [], withoutFolder: [] };
    if (!folderId) return groupByFolder(assets);
    return getItemsInFolder(assets, folderId);
  }, [content?.assets, folderId]);

  const currentFolderPath = useMemo(() => {
    if (!folderId) return [];
    const asset = content?.assets?.find((a) =>
      a.path.some((p) => p.id === folderId)
    );
    if (!asset) return [];
    const idx = asset.path.findIndex((p) => p.id === folderId);
    return asset.path.slice(0, idx + 1);
  }, [content?.assets, folderId]);

  const currentFolder =
    currentFolderPath.length > 0
      ? currentFolderPath[currentFolderPath.length - 1]
      : null;

  const campaignName = content?.name || content?.campaignName;

  const folders = useMemo(() => {
    let _folders = assetsGroupedByFolder.folders || [];
    if (searchText) {
      _folders = _folders
        .map((folder) => {
          return {
            ...folder,
            items: filterAssetsBySearchText(folder.items, searchText)
          };
        })
        .filter((folder) => !!folder.items.length);
    }
    return _folders;
  }, [assetsGroupedByFolder.folders, searchText]);

  const assetsList = useMemo(() => {
    let _assets = assetsGroupedByFolder.withoutFolder || [];
    if (searchText) {
      _assets = filterAssetsBySearchText(_assets, searchText);
    }
    return _assets;
  }, [assetsGroupedByFolder.withoutFolder, searchText]);

  const downloadAssets = useMemo(() => {
    const allAssetsInScope = folderId
      ? content?.assets?.filter((a) => a.path.some((p) => p.id === folderId))
      : content?.assets;
    return (
      allAssetsInScope?.filter((asset) => asset.asset?.permissions?.download) ||
      []
    );
  }, [content?.assets, folderId]);

  const assetsToDownload = useMemo(() => {
    if (!folderId) return undefined;
    return content?.assets
      ?.filter((a) => a.path.some((p) => p.id === folderId))
      .filter((asset) => !!asset.asset?.permissions.download);
  }, [content?.assets, folderId]);

  const isEmpty = useMemo(() => {
    return !assetsList?.length && !folders?.length && !folderId;
  }, [assetsList?.length, folders?.length, folderId]);

  const onSelectFolder = useCallback(
    (folderId: string) => {
      const updatedParams = new URLSearchParams(searchParams);
      updatedParams.set(FOLDER_PARAM, folderId);
      setSearchParams(updatedParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const onGoBack = useCallback(() => {
    const updatedParams = new URLSearchParams(searchParams);
    if (currentFolderPath.length <= 1) {
      updatedParams.delete(FOLDER_PARAM);
    } else {
      updatedParams.set(
        FOLDER_PARAM,
        currentFolderPath[currentFolderPath.length - 2].id
      );
    }
    setSearchParams(updatedParams, { replace: true });
  }, [searchParams, setSearchParams, currentFolderPath]);

  const onNavigateToFolder = useCallback(
    (targetFolderId: string | null) => {
      const updatedParams = new URLSearchParams(searchParams);
      if (!targetFolderId) {
        updatedParams.delete(FOLDER_PARAM);
      } else {
        updatedParams.set(FOLDER_PARAM, targetFolderId);
      }
      setSearchParams(updatedParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const onDownloadFiles = useCallback(() => {
    if (!downloadAssets.length) return;
    downloadAll(downloadAssets, `${campaignName}.zip`, folderId);
  }, [downloadAssets, campaignName, folderId]);

  useWebSocketDebouncedSubscription(
    (msg) => msg.payload.id,
    () => refetch(),
    1000,
    (msg) => {
      if (msg.payload.id !== reviewId) return false;
      return !msg.causedByMe;
    },
    'asset-review-updated',
    'asset-review-removed'
  );

  useWebSocketDebouncedSubscription(
    (msg) => msg.payload.campaignId,
    () => refetch(),
    1000,
    (msg) => !msg.causedByMe,
    'folder-created',
    'folder-updated',
    'folder-removed'
  );

  useWebSocketDebouncedSubscription(
    (msg) => msg.payload.campaignId,
    () => refetch(),
    1000,
    (msg) => {
      if (msg.payload.campaignId !== content?.campaignId) return false;
      if (msg.type === 'asset-updated' || msg.type === 'asset-removed') {
        if (!content?.assets.some((x) => x.id === msg.payload.assetId)) {
          return false;
        }
      }
      return !msg.causedByMe;
    },
    'asset-created',
    'asset-updated',
    'asset-removed'
  );

  return (
    <Layout
      style={{ background: '#1A1C1E', color: '#fff', minHeight: '100vh' }}
    >
      <Layout
        className="site-layout external-reviewer-media"
        style={{ background: '#1A1C1E' }}
      >
        <Header
          className="site-layout-background b-background-black"
          style={{
            padding: 0,
            width: '100%'
          }}
        >
          <div className="assets-list-header">
            <Row align="middle" justify="space-between">
              <Col>
                <LogoSvg />
              </Col>
              {!!downloadAssets.length && (
                <Col>
                  <Button
                    onClick={onDownloadFiles}
                    type="primary"
                    icon={<DownloadSvg />}
                  >
                    Download files
                  </Button>
                </Col>
              )}
            </Row>
          </div>
        </Header>
        <Content style={{ marginTop: 70 }}>
          <div className="assets-list">
            {currentFolder && (
              <>
                <div className="assets-list-breadcrumbs">
                  <Button onClick={onGoBack}>
                    <ArrowLeftSvg />
                  </Button>
                  <div
                    className="assets-list-breadcrumbs-item"
                    onClick={() => onNavigateToFolder(null)}
                  >
                    <Tooltip
                      title={campaignName}
                      overlayClassName="external_reviewer_tooltip"
                    >
                      {campaignName}
                    </Tooltip>
                  </div>
                  {currentFolderPath.map((pathItem, index) => {
                    const isLast = index === currentFolderPath.length - 1;
                    return (
                      <React.Fragment key={pathItem.id}>
                        <ArrowDownSvg className="assets-list-breadcrumbs-separator" />
                        <div
                          className={`assets-list-breadcrumbs-item${
                            isLast ? ' b-current' : ''
                          }`}
                          onClick={
                            isLast
                              ? undefined
                              : () => onNavigateToFolder(pathItem.id)
                          }
                        >
                          <Tooltip
                            title={pathItem.name}
                            overlayClassName="external_reviewer_tooltip"
                          >
                            {pathItem.name}
                          </Tooltip>
                        </div>
                      </React.Fragment>
                    );
                  })}
                </div>
                <div className="assets-list-title">
                  {currentFolder.isPrivate ? (
                    <PrivateFolderSvg />
                  ) : (
                    <FolderSvg width={32} height={32} />
                  )}
                  {currentFolder.name}
                  <div>
                    <Button
                      type="text"
                      size="small"
                      className="assets-list-btn-download"
                      disabled={!assetsToDownload?.length}
                      icon={<DownloadInvoiceSvg />}
                      onClick={() => {
                        if (!assetsToDownload?.length) return;
                        downloadAll(
                          assetsToDownload,
                          `${currentFolder.name}.zip`,
                          currentFolder.id
                        );
                      }}
                    />
                  </div>
                </div>
              </>
            )}
            {!currentFolder && (
              <div className="assets-list-title">
                <LinkSvg width={32} height={32} />
                {campaignName}
              </div>
            )}
            <ControlPanel />
            {viewValue === 'list' && (
              <ListView
                onSelectFolder={onSelectFolder}
                folders={folders}
                assetsList={assetsList}
                isOpenFolder={!!currentFolder}
              />
            )}
            {viewValue === 'tiles' && (
              <TilesView
                onSelectFolder={onSelectFolder}
                folders={folders}
                assetsList={assetsList}
                isOpenFolder={!!currentFolder}
              />
            )}
            {isEmpty && (
              <div className="assets-list-empty">
                <LottieComponent
                  className="assets-list-empty-icon"
                  view="addMediaInFolder"
                />
                <strong className="main-body-text main-body-text--bold">
                  No media found
                </strong>
              </div>
            )}
          </div>
        </Content>
      </Layout>
    </Layout>
  );
});
